import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User, Client, Device, SimCard, Renewal, Vehicle,
  DashboardStats, ApiResponse, LoginForm,
  ClientForm, DeviceForm, RenewalPaymentForm, Appointment,
  AppointmentCreationResponse, DashboardKPIs, DailyAction, DevicePosition, SystemStats, TraccarDevice
} from '../types';

// Las tres formas en que puede entrar el dinero de una instalación, más 'none' para lo que
// se registró como ingreso pero todavía no se ha cobrado. El instrumento (efectivo o
// transferencia) y el destinatario (el técnico o Driports) son preguntas distintas: una
// transferencia a la cuenta personal del técnico se la debe igual que el efectivo.
export type PaymentOption = 'cash' | 'transfer_technician' | 'transfer_company' | 'none';

export interface CobroEstado {
  amount: number;
  method: string;
  toTechnician?: boolean;
  etiqueta: string;
}

export interface CorreccionCobro {
  installationId: string;
  instalacionesAfectadas: number;
  anterior: CobroEstado;
  nuevo: CobroEstado;
  delta: number;
  nuevoEfectivoDelTecnico: number | null;
  adjustmentTransactionId: string | null;
  invoiceWarning: string | null;
}

export interface HistorialFinanciero {
  installationId: string;
  cliente: string | null;
  tecnico: string | null;
  instalacionesDelGrupo: number;
  cobroActual: CobroEstado & {
    loRecibioElTecnico: boolean;
    ultimaCorreccion: { fecha: string; por: string | null; motivo: string | null } | null;
  };
  correcciones: Array<{
    fecha: string;
    por: string;
    motivo: string | null;
    antes: CobroEstado | null;
    despues: CobroEstado | null;
    delta: number | null;
  }>;
  movimientos: Array<{
    fecha: string;
    tipo: string;
    monto: number;
    metodo: string | null;
    notas: string | null;
    por: string;
    saldoDespues: number | null;
  }>;
}

// La URL absoluta de un archivo subido al portal: comprobantes de pago, contratos, recibos.
// Los sirve el backend, no el sitio. Abrir '/uploads/...' tal cual lo busca en el dominio del
// frontend —Cloudflare Pages— y siempre da 404, que es por lo que ningún comprobante se podía
// ver desde la web aunque estuviera bien subido.
export const urlDeArchivo = (ruta?: string | null): string | null => {
  if (!ruta) return null;
  if (/^https?:\/\//i.test(ruta)) return ruta;
  const api = process.env.REACT_APP_API_URL || 'http://localhost:8090/api';
  return api.replace(/\/api\/?$/, '') + (ruta.startsWith('/') ? ruta : `/${ruta}`);
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8090/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔑 Token agregado a la request:', token.substring(0, 20) + '...');
        } else {
          console.log('⚠️ No hay token disponible para la request');
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Let React Router handle the redirect instead of forcing window.location
        }
        return Promise.reject(error);
      }
    );
  }

  private handleResponse<T>(response: AxiosResponse): T {
    return response.data;
  }

  // ============ AUTH ============
  async login(credentials: LoginForm): Promise<{ message: string; token: string; user: User }> {
    const response = await this.api.post('/auth/login', credentials);
    return this.handleResponse(response);
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/auth/me');
    return this.handleResponse(response);
  }

  // ============ DASHBOARD ============
  async getDashboardKPIs(): Promise<ApiResponse<DashboardKPIs>> {
    const response = await this.api.get('/dashboard/kpis');
    return this.handleResponse(response);
  }

  async getDailyActions(): Promise<ApiResponse<{
    date: string;
    totalActions: number;
    byPriority: Record<string, number>;
    actions: DailyAction[];
  }>> {
    const response = await this.api.get('/dashboard/daily-actions');
    return this.handleResponse(response);
  }

  async getDevicesMap(): Promise<ApiResponse<{
    devices: DevicePosition[];
    total: number;
    lastUpdate: string;
  }>> {
    const response = await this.api.get('/dashboard/devices-map');
    return this.handleResponse(response);
  }

  async getSystemStats(): Promise<ApiResponse<SystemStats>> {
    const response = await this.api.get('/dashboard/system-stats');
    return this.handleResponse(response);
  }

  async forcSync(type: 'devices' | 'positions' | 'offline-detection' | 'renewals'): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/dashboard/force-sync/${type}`);
    return this.handleResponse(response);
  }

  async syncAllData(): Promise<ApiResponse<any>> {
    const response = await this.api.post('/dashboard/sync-all');
    return this.handleResponse(response);
  }

  // ============ DEVICES ============
  async getDevices(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    clientId?: string;
    technicianId?: string;
    groupType?: string;
    vehicleId?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/devices', { params });
    return this.handleResponse(response);
  }


  async getDevicesByGroup(groupType: 'stock' | 'technician' | 'client' | 'general', technicianId?: string): Promise<ApiResponse<Device[]>> {
    const params = technicianId ? { technicianId } : {};
    const response = await this.api.get(`/devices/groups/${groupType}`, { params });
    return this.handleResponse(response);
  }

  // ========== NUEVOS MÉTODOS DE GRUPOS ==========

  async getGroups(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/groups');
    return this.handleResponse(response);
  }

  async getGroupsByType(type: 'stock' | 'technician' | 'general' | 'client'): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/groups/by-type/${type}`);
    return this.handleResponse(response);
  }

  async getGroupDevices(groupId: string, page = 1, limit = 25): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/groups/${groupId}/devices`, {
      params: { page, limit }
    });
    return this.handleResponse(response);
  }

  async createTechnicianGroup(technicianId: string, groupName?: string): Promise<ApiResponse<any>> {
    const response = await this.api.post('/groups/technician', {
      technicianId,
      groupName
    });
    return this.handleResponse(response);
  }

  async moveDeviceBetweenGroups(deviceId: string, targetGroupId: string, groupType: string): Promise<ApiResponse<any>> {
    const response = await this.api.put('/groups/move-device', {
      deviceId,
      targetGroupId,
      groupType
    });
    return this.handleResponse(response);
  }

  async getTechnicianDevices(technicianId: string): Promise<ApiResponse<Device[]>> {
    const response = await this.api.get(`/devices/technician/${technicianId}`);
    return this.handleResponse(response);
  }

  async getDevice(id: string): Promise<ApiResponse<Device & { lastPosition?: any }>> {
    const response = await this.api.get(`/devices/${id}`);
    return this.handleResponse(response);
  }

  async createDevice(device: DeviceForm): Promise<ApiResponse<Device>> {
    const response = await this.api.post('/devices', device);
    return this.handleResponse(response);
  }

  async updateDevice(id: string, device: Partial<DeviceForm>): Promise<ApiResponse<Device>> {
    const response = await this.api.put(`/devices/${id}`, device);
    return this.handleResponse(response);
  }

  // Quick assign methods
  async searchDeviceByIMEI(imei: string): Promise<ApiResponse<Device>> {
    const response = await this.api.get(`/devices/search-imei/${imei}`);
    return this.handleResponse(response);
  }

  async quickAssignDevice(data: {
    imei: string;
    clientId: string;
    name: string;
    model: string;
    installationDate: string;
  }): Promise<ApiResponse<Device>> {
    const response = await this.api.post('/devices/quick-assign', data);
    return this.handleResponse(response);
  }

  async updateDeviceStatus(id: string, status: string): Promise<ApiResponse<Device>> {
    const response = await this.api.patch(`/devices/${id}/status`, { status });
    return this.handleResponse(response);
  }

  async assignDeviceToTechnician(deviceId: string, technicianId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/devices/${deviceId}/assign-technician`, { technicianId });
    return this.handleResponse(response);
  }

  async assignDeviceToClient(deviceId: string, clientId: string, vehicleId?: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/devices/${deviceId}/assign-client`, { clientId, vehicleId });
    return this.handleResponse(response);
  }

  async returnDeviceToStock(deviceId: string, notes?: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/devices/${deviceId}/return-to-stock`, { notes });
    return this.handleResponse(response);
  }

  async sendDeviceCommand(deviceId: string, command: string, attributes?: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/devices/${deviceId}/commands/${command}`, { attributes });
    return this.handleResponse(response);
  }

  async getDevicePosition(deviceId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/devices/${deviceId}/position`);
    return this.handleResponse(response);
  }

  async getDeviceStatsbyGroup(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/devices/stats/groups');
    return this.handleResponse(response);
  }

  async getDeviceStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/devices/stats/groups');
    return this.handleResponse(response);
  }

  async syncDevices(): Promise<ApiResponse<any>> {
    const response = await this.api.post('/devices/sync');
    return this.handleResponse(response);
  }

  // ========== MÉTODOS DE REVISIONES ==========

  async getDeviceRevisions(params?: {
    page?: number;
    limit?: number;
    priority?: string;
    technicianId?: string;
    reason?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/device-revisions', { params });
    return this.handleResponse(response);
  }

  async createDeviceRevision(data: {
    deviceId: string;
    reason: 'maintenance' | 'client_request' | 'quality_check' | 'malfunction';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    notes?: string;
    technicianId?: string;
    estimatedCompletionDate?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/device-revisions', data);
    return this.handleResponse(response);
  }

  async assignRevisionTechnician(deviceId: string, technicianId: string): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/device-revisions/${deviceId}/assign-technician`, {
      technicianId
    });
    return this.handleResponse(response);
  }

  async completeDeviceRevision(deviceId: string, data: {
    completionNotes?: string;
    returnToStock?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/device-revisions/${deviceId}/complete`, data);
    return this.handleResponse(response);
  }

  async cancelDeviceRevision(deviceId: string, cancellationReason: string): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/device-revisions/${deviceId}/cancel`, {
      cancellationReason
    });
    return this.handleResponse(response);
  }

  async getDeviceRevisionHistory(deviceId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/device-revisions/${deviceId}/history`);
    return this.handleResponse(response);
  }

  // ============ VEHICLES ============
  async getVehicles(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    clientId?: string;
    hasDevice?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/vehicles', { params });
    return this.handleResponse(response);
  }

  async getVehicle(id: string): Promise<ApiResponse<Vehicle>> {
    const response = await this.api.get(`/vehicles/${id}`);
    return this.handleResponse(response);
  }

  async createVehicle(vehicle: any): Promise<ApiResponse<Vehicle>> {
    const response = await this.api.post('/vehicles', vehicle);
    return this.handleResponse(response);
  }

  async updateVehicle(id: string, vehicle: any): Promise<ApiResponse<Vehicle>> {
    const response = await this.api.put(`/vehicles/${id}`, vehicle);
    return this.handleResponse(response);
  }

  async assignDeviceToVehicle(vehicleId: string, deviceId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/vehicles/${vehicleId}/assign-device`, { deviceId });
    return this.handleResponse(response);
  }

  async unassignDeviceFromVehicle(vehicleId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/vehicles/${vehicleId}/unassign-device`);
    return this.handleResponse(response);
  }

  async getVehiclesByClient(clientId: string): Promise<ApiResponse<Vehicle[]>> {
    const response = await this.api.get(`/vehicles/client/${clientId}`);
    return this.handleResponse(response);
  }

  async deleteVehicle(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/vehicles/${id}`);
    return this.handleResponse(response);
  }

  // ============ CLIENTS ============
  async getClients(page?: number, limit?: number, search?: string): Promise<ApiResponse<any>> {
    console.log('🔄 API Service - getClients llamado con params:', { page, limit, search });
    const params = { page, limit, search };
    const response = await this.api.get('/clients', { params });
    console.log('📥 API Service - getClients response:', response.data);
    return this.handleResponse(response);
  }

  async lookupClientByPhone(phone: string): Promise<any> {
    const response = await this.api.get(`/clients/lookup/phone/${phone}`);
    return response.data;
  }

  async getClient(id: string): Promise<ApiResponse<Client>> {
    console.log('🔄 API Service - getClient llamado con ID:', id);
    const response = await this.api.get(`/clients/${id}`);
    console.log('📥 API Service - getClient response:', response.data);
    return this.handleResponse(response);
  }

  async createClient(client: ClientForm): Promise<ApiResponse<Client>> {
    const response = await this.api.post('/clients', client);
    return this.handleResponse(response);
  }

  async updateClient(id: string, client: Partial<ClientForm>): Promise<ApiResponse<Client>> {
    const response = await this.api.put(`/clients/${id}`, client);
    return this.handleResponse(response);
  }

  async deleteClient(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/clients/${id}`);
    return this.handleResponse(response);
  }

  async deactivateClient(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.patch(`/clients/${id}/deactivate`);
    return this.handleResponse(response);
  }

  async getClientStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/clients/stats/summary');
    return this.handleResponse(response);
  }

  // ============ USERS ============
  async getUsers(params?: any): Promise<ApiResponse<any>> {
    const response = await this.api.get('/users', { params });
    return this.handleResponse(response);
  }

  async getTechnicians(): Promise<ApiResponse<User[]>> {
    console.log('🔄 API Service - getTechnicians llamado');
    const response = await this.api.get('/users?role=technician');
    console.log('📥 API Service - getTechnicians RAW response:', response);
    console.log('📥 API Service - getTechnicians response.data:', response.data);
    return this.handleResponse(response);
  }

  async getUser(id: string): Promise<ApiResponse<User>> {
    const response = await this.api.get(`/users/${id}`);
    return this.handleResponse(response);
  }

  async createUser(user: any): Promise<ApiResponse<User>> {
    const response = await this.api.post('/users', user);
    return this.handleResponse(response);
  }

  async updateUser(id: string, user: any): Promise<ApiResponse<User>> {
    const response = await this.api.put(`/users/${id}`, user);
    return this.handleResponse(response);
  }

  async deleteUser(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/users/${id}`);
    return this.handleResponse(response);
  }

  // ============ RENEWALS ============
  async getRenewals(page?: number, limit?: number, status?: string, priority?: string): Promise<ApiResponse<any>> {
    const params = { page, limit, status, priority };
    const response = await this.api.get('/renewals', { params });
    return this.handleResponse(response);
  }

  async updateRenewalStatus(id: string, status: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/renewals/${id}/status`, { status });
    return this.handleResponse(response);
  }

  async uploadPaymentEvidence(id: string, formData: FormData): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/renewals/${id}/payment-evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return this.handleResponse(response);
  }

  async getRenewal(id: string): Promise<ApiResponse<Renewal>> {
    const response = await this.api.get(`/renewals/${id}`);
    return this.handleResponse(response);
  }

  async createRenewal(renewal: any): Promise<ApiResponse<Renewal>> {
    const response = await this.api.post('/renewals', renewal);
    return this.handleResponse(response);
  }

  async updateRenewal(id: string, renewal: any): Promise<ApiResponse<Renewal>> {
    const response = await this.api.put(`/renewals/${id}`, renewal);
    return this.handleResponse(response);
  }

  async processCompleteRenewal(formData: FormData): Promise<ApiResponse<any>> {
    const response = await this.api.post('/renewals/process-complete-renewal', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return this.handleResponse(response);
  }

  async getCompletedRenewals(page?: number, limit?: number): Promise<ApiResponse<any>> {
    const params = { page, limit };
    const response = await this.api.get('/renewals/completed-history', { params });
    return this.handleResponse(response);
  }

  async testCompletedRenewals(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/renewals/completed-test');
    return this.handleResponse(response);
  }

  async pingRenewals(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/renewals/ping');
    return this.handleResponse(response);
  }

  async healthRenewals(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/renewals/health-check');
    return this.handleResponse(response);
  }

  async notifyRenewal(id: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/renewals/${id}/contact`, data);
    return this.handleResponse(response);
  }

  async recordRenewalPayment(id: string, payment: RenewalPaymentForm): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/renewals/${id}/confirm-payment`, payment);
    return this.handleResponse(response);
  }

  async getUpcomingRenewals(days: number = 30): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/renewals/due/soon?days=${days}`);
    return this.handleResponse(response);
  }

  // Cancelar renovación (cliente decidió no renovar)
  async cancelRenewal(id: string, data: { reason?: string; notes?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/renewals/${id}/cancel`, data);
    return this.handleResponse(response);
  }

  // Reactivar renovación cancelada
  async reactivateRenewal(id: string, data?: { notes?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/renewals/${id}/reactivate`, data || {});
    return this.handleResponse(response);
  }

  // Obtener renovaciones canceladas
  async getDeclinedRenewals(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/renewals/declined-renewals');
    return this.handleResponse(response);
  }

  async getExpirationForecast(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/renewals/projections/expiration-forecast');
    return this.handleResponse(response);
  }

  // ============ REPORTS ============
  async getOfflineDevicesReport(hours: number = 2): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/dashboard/reports/offline-devices?hours=${hours}`);
    return this.handleResponse(response);
  }

  async getPendingRenewalsReport(days: number = 30): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/dashboard/reports/pending-renewals?days=${days}`);
    return this.handleResponse(response);
  }

  // ============ CONTACT MANAGEMENT ============
  async getContactHistory(renewalId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/renewals/${renewalId}/contacts`);
    return this.handleResponse(response);
  }

  async createContact(renewalId: string, formData: FormData): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/renewals/${renewalId}/contacts`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return this.handleResponse(response);
  }

  async updateContactStatus(renewalId: string, contactId: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/renewals/${renewalId}/contacts/${contactId}/update-status`, data);
    return this.handleResponse(response);
  }

  async getContactStats(renewalId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/renewals/${renewalId}/contacts/stats`);
    return this.handleResponse(response);
  }

  // ============ SIM CARDS ============
  async getSimCards(page?: number, limit?: number, search?: string, status?: string): Promise<ApiResponse<any>> {
    const params = { page, limit, search, status };
    const response = await this.api.get('/simcards', { params });
    return this.handleResponse(response);
  }

  async updateSimCardStatus(id: string, status: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/simcards/${id}/status`, { status });
    return this.handleResponse(response);
  }

  async syncSimCardWithSimbase(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/simcards/${id}/sync`);
    return this.handleResponse(response);
  }

  async resetSimCard(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/simcards/${id}/reset`);
    return this.handleResponse(response);
  }

  async getSimCard(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/simcards/${id}`);
    return this.handleResponse(response);
  }

  async getSimCardUsage(id: string, from?: string, to?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await this.api.get(`/simcards/${id}/usage`, { params });
    return this.handleResponse(response);
  }

  async getSimCardCdrs(id: string, from?: string, to?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await this.api.get(`/simcards/${id}/cdrs`, { params });
    return this.handleResponse(response);
  }

  async activateSimCard(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/simcards/${id}/activate`);
    return this.handleResponse(response);
  }

  async deactivateSimCard(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/simcards/${id}/deactivate`);
    return this.handleResponse(response);
  }

  async createSimCard(sim: any): Promise<ApiResponse<SimCard>> {
    const response = await this.api.post('/simcards', sim);
    return this.handleResponse(response);
  }

  async updateSimCard(id: string, sim: any): Promise<ApiResponse<SimCard>> {
    const response = await this.api.put(`/simcards/${id}`, sim);
    return this.handleResponse(response);
  }

  // ============ APPOINTMENTS ============
  async getAppointments(params?: any): Promise<ApiResponse<any>> {
    const response = await this.api.get('/appointments', { params });
    return this.handleResponse(response);
  }

  async getAppointment(id: string): Promise<ApiResponse<Appointment>> {
    const response = await this.api.get(`/appointments/${id}`);
    return this.handleResponse(response);
  }

  async createAppointment(appointment: any): Promise<ApiResponse<AppointmentCreationResponse>> {
    const response = await this.api.post('/appointments', appointment);
    return this.handleResponse(response);
  }

  // Cita tentativa (agendada desde el CRM, sin técnico aún)
  async createTentativeAppointment(data: { leadName?: string; leadPhone?: string; scheduledDate: string; timeSlot?: string; notes?: string; clientId?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/appointments/tentative', data);
    return this.handleResponse(response);
  }

  async confirmTentativeAppointment(id: string, data: { technicianId: string; timeSlot?: string; scheduledDate?: string; address?: { street?: string; city?: string; coordinates?: { lat: number; lng: number } }; notes?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/appointments/${id}/confirm-tentative`, data);
    return this.handleResponse(response);
  }

  // Editar una cita tentativa (fecha, hora, nombre, teléfono, notas) sin confirmarla
  async updateTentativeAppointment(id: string, data: { scheduledDate?: string; timeSlot?: string; leadName?: string; leadPhone?: string; notes?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/appointments/${id}/tentative`, data);
    return this.handleResponse(response);
  }


  // ============ EXPENSES / GASTOS ============
  async getExpenses(params?: any): Promise<ApiResponse<any>> {
    const response = await this.api.get('/expenses', { params });
    return this.handleResponse(response);
  }

  async createExpense(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/expenses', data);
    return this.handleResponse(response);
  }

  async deleteExpense(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/expenses/${id}`);
    return this.handleResponse(response);
  }

  // Resumen de gastos mes por mes, con desglose por categoría de cada uno
  async getExpensesMonthly(months = 12): Promise<ApiResponse<any>> {
    const response = await this.api.get('/expenses/monthly', { params: { months } });
    return this.handleResponse(response);
  }

  // ============ PRESUPUESTOS ============
  async getBudgets(month?: string): Promise<ApiResponse<any>> {
    const response = await this.api.get('/expenses/budgets', { params: month ? { month } : {} });
    return this.handleResponse(response);
  }

  async saveBudget(data: { category: string; month: string; amount: number; currency?: string; exchangeRate?: number; notes?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.put('/expenses/budgets', data);
    return this.handleResponse(response);
  }

  async deleteBudget(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/expenses/budgets/${id}`);
    return this.handleResponse(response);
  }

  // ============ GASTOS DEL TECNICO ============
  async getMyExpenses(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/technician-portal/my-expenses');
    return this.handleResponse(response);
  }

  async createMyExpense(data: { category: string; amount: number; description?: string; date?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/technician-portal/my-expense', data);
    return this.handleResponse(response);
  }

  // ============ ENTREGAS DE EFECTIVO DEL TECNICO ============
  // Qué pasó desde la última vez que quedó en cero: instalaciones con su fecha, gastos y
  // entregas. Es la línea de tiempo que zanja los "yo eso lo instalé después de entregarte".
  async getMyPeriod(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/technician-portal/my-period');
    return this.handleResponse(response);
  }

  // El técnico declara la entrega; queda pendiente hasta que el admin la apruebe.
  async declareMyCashDelivery(data: {
    amount: number;
    deliveredToName: string;
    date?: string;
    notes?: string;
    deliveryMethod?: 'cash' | 'bank_transfer';
    // `amount` es lo entregado EN MANO. Con esto en true el servidor le suma la comisión que
    // el técnico se cobró de ese mismo dinero para saber cuánto efectivo deja de deber.
    deductCommission?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/technician-portal/my-cash-delivery', data);
    return this.handleResponse(response);
  }

  // Mismo desglose que ve el técnico, pero para el admin y de cualquier técnico.
  async getTechnicianPeriod(technicianId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/technician-finance/period/${technicianId}`);
    return this.handleResponse(response);
  }

  async getPendingDeliveries(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/technician-finance/pending-deliveries');
    return this.handleResponse(response);
  }

  async resolveDelivery(id: string, approve: boolean, reason?: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/technician-finance/deliveries/${id}/resolve`, { approve, reason });
    return this.handleResponse(response);
  }

  async updateAppointment(id: string, appointment: any): Promise<ApiResponse<AppointmentCreationResponse>> {
    const response = await this.api.put(`/appointments/${id}`, appointment);
    return this.handleResponse(response);
  }

  async deleteAppointment(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/appointments/${id}`);
    return this.handleResponse(response);
  }

  async getNotInstalledDevices(): Promise<ApiResponse<TraccarDevice[]>> {
    const response = await this.api.get('/appointments/devices/not-installed');
    return this.handleResponse(response);
  }

  // Resolver/importar un equipo por IMEI (completo o últimos 4+ dígitos). Lo trae de Traccar y lo
  // importa al portal si hace falta, para poder instalarlo aunque no esté en el grupo stock.
  async resolveDeviceByImei(imei: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/appointments/devices/resolve/${encodeURIComponent(imei)}`);
    return this.handleResponse(response);
  }

  async closeAppointment(id: string, data: {
    clientId?: string;
    newClient?: { name: string; email: string; phoneNumber?: string; address?: string; city?: string };
    completionNotes?: string;
    deviceIds: (number | string)[];
    deviceNames: string[];
    paymentMethod?: string;
    paymentAmount?: number | string;
    paymentReference?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/appointments/${id}/close`, data);
    return this.handleResponse(response);
  }

  // Instalar SIN cita previa (crea cliente + usuario + asigna dispositivo + registra pago/comisión)
  async quickCloseInstallation(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/appointments/quick-close`, data);
    return this.handleResponse(response);
  }

  // Reenviar / regenerar credenciales de acceso del cliente (arregla email mal escrito o clave no recibida)
  async resendCredentials(data: { clientId: string; email?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/appointments/resend-credentials`, data);
    return this.handleResponse(response);
  }

  // ============ LEGACY SUPPORT ============
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    // Mantener compatibilidad con el dashboard anterior
    const response = await this.api.get('/dashboard/kpis');
    return this.handleResponse(response);
  }

  async getAlerts(params?: { severity?: string; read?: string; limit?: number; page?: number }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/notifications', { params });
    return this.handleResponse(response);
  }

  async getNotificationSummary(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/notifications/summary');
    return this.handleResponse(response);
  }

  async markNotificationRead(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/notifications/${id}/read`);
    return this.handleResponse(response);
  }

  async markAllNotificationsRead(): Promise<ApiResponse<any>> {
    const response = await this.api.patch('/notifications/read-all');
    return this.handleResponse(response);
  }

  async dismissNotification(id: string, reason?: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/notifications/${id}/dismiss`, { reason });
    return this.handleResponse(response);
  }

  async generateNotifications(): Promise<ApiResponse<any>> {
    const response = await this.api.post('/notifications/generate');
    return this.handleResponse(response);
  }

  // ============ SIMBASE FINANCE ============
  async getSimbaseBalance(fresh?: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/simbase/balance${fresh ? '?fresh=true' : ''}`);
    return this.handleResponse(response);
  }

  async syncSimbaseFinance(): Promise<ApiResponse<any>> {
    const response = await this.api.post('/simbase/balance/sync');
    return this.handleResponse(response);
  }

  // ============ ACTIVITY ============
  async getRecentActivity(limit: number = 5): Promise<ApiResponse<{ activities: any[] }>> {
    // Mock data for now since backend doesn't have this endpoint yet
    return {
      success: true,
      data: {
        activities: []
      },
      message: 'Recent activity retrieved successfully'
    };
  }

  // ============ INSTALLATIONS ============
  async getInstallations(page: number = 1, limit: number = 25, filters?: any): Promise<ApiResponse<any>> {
    let url = `/installations?page=${page}&limit=${limit}`;

    if (filters) {
      if (filters.technicianId) url += `&technicianId=${filters.technicianId}`;
      if (filters.clientId) url += `&clientId=${filters.clientId}`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.startDate) url += `&startDate=${filters.startDate}`;
      if (filters.endDate) url += `&endDate=${filters.endDate}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
    }

    const response = await this.api.get(url);
    return this.handleResponse(response);
  }

  async getInstallationById(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/installations/${id}`);
    return this.handleResponse(response);
  }

  async getInstallationStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/installations/stats');
    return this.handleResponse(response);
  }

  async getTechnicianStats(technicianId: string, startDate?: string, endDate?: string): Promise<ApiResponse<any>> {
    let url = `/installations/technician/${technicianId}/stats`;

    const params = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    const response = await this.api.get(url);
    return this.handleResponse(response);
  }

  async completeInstallation(appointmentId: string, installationData: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/appointments/${appointmentId}/complete-installation`, installationData);
    return this.handleResponse(response);
  }

  async markInstallationPaid(installationId: string, paymentReference: string): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/installations/${installationId}/payment`, {
      paymentReference
    });
    return this.handleResponse(response);
  }

  async addInstallationRating(installationId: string, rating: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/installations/${installationId}/rating`, rating);
    return this.handleResponse(response);
  }

  async confirmClientPayment(installationId: string, data: { paymentMethod: 'cash' | 'bank_transfer'; paymentAmount: number; paymentReference?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/installations/${installationId}/confirm-client-payment`, data);
    return this.handleResponse(response);
  }

  async updateInstallation(installationId: string, updateData: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/installations/${installationId}`, updateData);
    return this.handleResponse(response);
  }

  // Corrige el monto cobrado cuando el técnico digitó mal el precio al cerrar.
  // Ajusta en cascada la instalación, el efectivo que debe el técnico y deja un ajuste en su historial.
  async correctInstallationAmount(
    installationId: string,
    data: { newAmount: number; reason: string }
  ): Promise<ApiResponse<{
    installationId: string;
    oldAmount: number;
    newAmount: number;
    delta: number;
    method: string;
    newCashOwed: number | null;
    adjustmentTransactionId: string | null;
    invoiceWarning: string | null;
  }>> {
    const response = await this.api.put(`/installations/${installationId}/correct-amount`, data);
    return this.handleResponse(response);
  }

  // Corrige el cobro completo: el monto, el instrumento y sobre todo A QUIÉN le llegó el
  // dinero. El backend mueve el efectivo que el técnico debe, deja un ajuste en su historial
  // y un registro de auditoría con el antes y el después.
  async correctInstallationPayment(
    installationId: string,
    data: { newAmount?: number; newPaymentOption?: PaymentOption; reason: string }
  ): Promise<ApiResponse<CorreccionCobro>> {
    const response = await this.api.put(`/installations/${installationId}/correct-payment`, data);
    return this.handleResponse(response);
  }

  // Qué se registró y qué se corrigió en esta instalación.
  async getInstallationFinancialHistory(installationId: string): Promise<ApiResponse<HistorialFinanciero>> {
    const response = await this.api.get(`/installations/${installationId}/financial-history`);
    return this.handleResponse(response);
  }

  // ============ TECHNICIAN FINANCE ============
  async getTechnicianFinanceSummary(): Promise<ApiResponse<{
    technicians: Array<{
      id: string;
      name: string;
      email: string;
      cashOwedToCompany: number;
      commissionsOwedByCompany: number;
      netBalance: number;
      installationsThisMonth: number;
      thisMonth: {
        cashCollected: number;
        cashDelivered: number;
        commissionsEarned: number;
        commissionsPaid: number;
      };
      lastTransaction: string | null;
    }>;
    totals: {
      totalCashWithTechnicians: number;
      totalCommissionsPending: number;
    };
  }>> {
    const response = await this.api.get('/technician-finance/summary');
    return this.handleResponse(response);
  }

  async getTechnicianBalance(technicianId: string): Promise<ApiResponse<{
    technician: {
      id: string;
      name: string;
      email: string;
      commissionRate: number;
    };
    balance: {
      cashOwedToCompany: number;
      commissionsOwedByCompany: number;
      netBalance: number;
    };
    thisMonth: {
      cashCollected: number;
      cashDelivered: number;
      commissionsEarned: number;
      commissionsPaid: number;
      installations: number;
    };
  }>> {
    const response = await this.api.get(`/technician-finance/balance/${technicianId}`);
    return this.handleResponse(response);
  }

  async getMyBalance(): Promise<ApiResponse<{
    balance: {
      cashOwedToCompany: number;
      commissionsOwedByCompany: number;
      netBalance: number;
    };
    thisMonth: {
      cashCollected: number;
      cashDelivered: number;
      commissionsEarned: number;
      commissionsPaid: number;
      installations: number;
    };
  }>> {
    const response = await this.api.get('/technician-finance/my-balance');
    return this.handleResponse(response);
  }

  async getTechnicianTransactions(
    technicianId: string,
    params?: {
      page?: number;
      limit?: number;
      type?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<ApiResponse<{
    transactions: Array<{
      _id: string;
      technicianId: string;
      transactionType: string;
      amount: number;
      currency: string;
      paymentMethod?: string;
      paymentReference?: string;
      notes?: string;
      receivedBy?: { _id: string; name: string; email: string };
      createdBy: { _id: string; name: string; email: string };
      balanceAfter: {
        cashOwedToCompany: number;
        commissionsOwedByCompany: number;
      };
      status: string;
      createdAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    const url = `/technician-finance/transactions/${technicianId}${queryString ? `?${queryString}` : ''}`;
    const response = await this.api.get(url);
    return this.handleResponse(response);
  }

  async registerCashCollection(data: {
    technicianId: string;
    amount: number;
    clientId?: string;
    renewalId?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/technician-finance/cash-collection', data);
    return this.handleResponse(response);
  }

  async registerCashDelivery(data: {
    technicianId: string;
    amount: number;
    paymentReference?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/technician-finance/cash-delivery', data);
    return this.handleResponse(response);
  }

  async payTechnicianCommission(data: {
    technicianId: string;
    amount: number;
    paymentMethod: 'cash' | 'bank_transfer' | 'check';
    paymentReference?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/technician-finance/pay-commission', data);
    return this.handleResponse(response);
  }

  async createFinanceAdjustment(data: {
    technicianId: string;
    adjustmentType: 'cash' | 'commission';
    amount: number;
    reason: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/technician-finance/adjustment', data);
    return this.handleResponse(response);
  }

  async getUnpaidInstallations(technicianId: string): Promise<ApiResponse<{
    installations: Array<{
      _id: string;
      installationDate: string;
      commission: { amount: number; isPaid: boolean };
      clientId?: { _id: string; name: string; code: string };
      deviceId?: { _id: string; imei: string };
    }>;
    totalPending: number;
    count: number;
  }>> {
    const response = await this.api.get(`/technician-finance/unpaid-installations/${technicianId}`);
    return this.handleResponse(response);
  }

  // ============ HEALTH CHECK ============
  async healthCheck(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/health');
    return this.handleResponse(response);
  }

  // ============ ADMIN: AUDIT LOGS ============
  async getAuditLogs(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/admin/audit', { params });
    return this.handleResponse(response);
  }

  // Vista agrupada: un registro por incidente (eventId) con su timeline,
  // en lugar de una fila por cada paso interno del worker.
  async getAuditLogsGrouped(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/admin/audit/grouped', { params });
    return this.handleResponse(response);
  }

  async getAuditStats(): Promise<any> {
    const response = await this.api.get('/admin/audit/stats');
    return this.handleResponse(response);
  }

  async getAuditLog(id: string): Promise<any> {
    const response = await this.api.get(`/admin/audit/${id}`);
    return this.handleResponse(response);
  }

  // ============ ADMIN: SUBSCRIPTIONS ============
  async getAdminSubscriptions(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/admin/subscriptions', { params });
    return this.handleResponse(response);
  }

  async getSubscriptionStats(): Promise<any> {
    const response = await this.api.get('/admin/subscriptions/stats');
    return this.handleResponse(response);
  }

  // Lo que reporta la app sobre sus compras: quién quiso pagar, si pudo y dónde se trabó.
  async getPurchaseAttempts(dias = 7): Promise<any> {
    const response = await this.api.get('/admin/subscriptions/intentos-compra', { params: { dias } });
    return this.handleResponse(response);
  }

  // Embudo de upsell: quién toca una función bloqueada en la app, cuántos llegan a los planes y cuántos compran.
  async getUpsellFunnel(dias = 30): Promise<any> {
    const response = await this.api.get('/admin/subscriptions/funnel', { params: { dias } });
    return this.handleResponse(response);
  }

  async getAdminSubscription(userId: string): Promise<any> {
    const response = await this.api.get(`/admin/subscriptions/${userId}`);
    return this.handleResponse(response);
  }

  async updateSubscription(userId: string, data: any): Promise<any> {
    const response = await this.api.put(`/admin/subscriptions/${userId}`, data);
    return this.handleResponse(response);
  }

  async deleteSubscription(userId: string): Promise<any> {
    const response = await this.api.delete(`/admin/subscriptions/${userId}`);
    return this.handleResponse(response);
  }

  async createSubscription(data: any): Promise<any> {
    const response = await this.api.post('/admin/subscriptions', data);
    return this.handleResponse(response);
  }

  async extendSubscription(userId: string, days: number): Promise<any> {
    const response = await this.api.post(`/admin/subscriptions/${userId}/extend`, { days });
    return this.handleResponse(response);
  }

  async suspendSubscription(userId: string): Promise<any> {
    const response = await this.api.post(`/admin/subscriptions/${userId}/suspend`);
    return this.handleResponse(response);
  }

  async reactivateSubscription(userId: string): Promise<any> {
    const response = await this.api.post(`/admin/subscriptions/${userId}/reactivate`);
    return this.handleResponse(response);
  }

  async getSubscriptionPlans(): Promise<any> {
    const response = await this.api.get('/admin/subscriptions/plans');
    return this.handleResponse(response);
  }

  async searchClientsForSubscription(q: string): Promise<any> {
    const response = await this.api.get('/admin/subscriptions/search-clients', { params: { q } });
    return this.handleResponse(response);
  }

  async getSubscriptionConfig(userId: string): Promise<any> {
    const response = await this.api.get(`/admin/subscriptions/${userId}/config`);
    return this.handleResponse(response);
  }

  async updateSubscriptionConfig(userId: string, data: any): Promise<any> {
    const response = await this.api.put(`/admin/subscriptions/${userId}/config`, data);
    return this.handleResponse(response);
  }

  // ============ TECHNICIAN PORTAL ============
  async getMyAppointments(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/technician-portal/my-appointments', { params });
    return this.handleResponse(response);
  }

  async getMyStats(): Promise<any> {
    const response = await this.api.get('/technician-portal/my-stats');
    return this.handleResponse(response);
  }

  async getMyTransactions(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/technician-portal/my-transactions', { params });
    return this.handleResponse(response);
  }

  async getMyCalendar(start: string, end: string): Promise<any> {
    const response = await this.api.get('/technician-portal/my-calendar', { params: { start, end } });
    return this.handleResponse(response);
  }

  async createMyAppointment(data: any): Promise<any> {
    const response = await this.api.post('/technician-portal/create-appointment', data);
    return this.handleResponse(response);
  }

  async getMyClients(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/technician-portal/my-clients', { params });
    return this.handleResponse(response);
  }
  // ============ INVOICES / PDF ============
  async generateInvoicePdf(data: any): Promise<Blob> {
    const response = await this.api.post('/invoices/generate-pdf', data, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getInstallationsForInvoice(year: number, month: number, clientId?: string): Promise<ApiResponse<any>> {
    const params = clientId ? { clientId } : {};
    const response = await this.api.get(`/invoices/generate/installations/${year}/${month}`, { params });
    return this.handleResponse(response);
  }

  async getRenewalsForInvoice(year: number, month: number, clientId?: string): Promise<ApiResponse<any>> {
    const params = clientId ? { clientId } : {};
    const response = await this.api.get(`/invoices/generate/renewals/${year}/${month}`, { params });
    return this.handleResponse(response);
  }

  async getInvoices(page = 1, limit = 20, params?: any): Promise<any> {
    const response = await this.api.get('/invoices', { params: { page, limit, ...params } });
    return response.data;
  }

  async getNextInvoiceNumber(clientId: string): Promise<any> {
    const response = await this.api.get(`/invoices/next-number/${clientId}`);
    return response.data;
  }

  async saveInvoice(data: any): Promise<any> {
    const response = await this.api.post('/invoices/save-and-generate', data);
    return response.data;
  }

  // Editar una factura ya guardada. Es lo que evita tener que emitir una nueva cada vez que
  // hay que corregir un renglón: mientras no esté pagada, se modifica la misma.
  async updateInvoice(invoiceId: string, data: any): Promise<any> {
    const response = await this.api.put(`/invoices/${invoiceId}`, data);
    return response.data;
  }

  // Cerrar la factura con el pago recibido. Va como multipart porque lleva el comprobante
  // adjunto. A partir de aquí la factura no se edita ni se saca de caja.
  //
  // La cabecera va explícita y NO se puede quitar: esta instancia de axios nace con
  // 'application/json' por defecto, y axios 1.x, al ver ese tipo, convierte el FormData a JSON
  // (`transformRequest` → `formDataToJSON`). El archivo se pierde en el navegador, llegan solo
  // los campos de texto, y el backend responde 400 pidiendo un comprobante que sí se adjuntó.
  async payInvoice(
    invoiceId: string,
    data: { method: string; transactionId?: string; receipt?: File | null }
  ): Promise<any> {
    const form = new FormData();
    form.append('method', data.method);
    if (data.transactionId) form.append('transactionId', data.transactionId);
    if (data.receipt) form.append('paymentEvidence', data.receipt);
    const response = await this.api.post(`/invoices/${invoiceId}/payment`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Borrar una factura emitida de más. El backend rechaza las ya cobradas.
  async deleteInvoice(invoiceId: string): Promise<any> {
    const response = await this.api.delete(`/invoices/${invoiceId}`);
    return response.data;
  }

  // ============ COTIZACIONES ============
  // Una cotización todavía no es plata: no toca caja hasta que se convierte en factura.

  async getQuotes(page = 1, limit = 25, params?: any): Promise<any> {
    const response = await this.api.get('/quotes', { params: { page, limit, ...params } });
    return response.data;
  }

  async getQuote(quoteId: string): Promise<any> {
    const response = await this.api.get(`/quotes/${quoteId}`);
    return response.data;
  }

  async getNextQuoteNumber(): Promise<any> {
    const response = await this.api.get('/quotes/next-number');
    return response.data;
  }

  async createQuote(data: any): Promise<any> {
    const response = await this.api.post('/quotes', data);
    return response.data;
  }

  async updateQuote(quoteId: string, data: any): Promise<any> {
    const response = await this.api.put(`/quotes/${quoteId}`, data);
    return response.data;
  }

  // En qué quedó la oferta: enviada, aceptada, rechazada.
  async setQuoteStatus(quoteId: string, status: string): Promise<any> {
    const response = await this.api.put(`/quotes/${quoteId}/status`, { status });
    return response.data;
  }

  // El PDF de algo que aún no se guardó, para enseñárselo al cliente sin quemar un número
  // de la serie.
  async previewQuotePdf(data: any): Promise<Blob> {
    const response = await this.api.post('/quotes/preview-pdf', data, { responseType: 'blob' });
    return response.data;
  }

  async downloadQuotePdf(quoteId: string): Promise<Blob> {
    const response = await this.api.get(`/quotes/${quoteId}/pdf`, { responseType: 'blob' });
    return response.data;
  }

  // El punto donde la oferta pasa a ser cobro. Exige un cliente registrado.
  async convertQuoteToInvoice(quoteId: string, data: any = {}): Promise<any> {
    const response = await this.api.post(`/quotes/${quoteId}/convert-to-invoice`, data);
    return response.data;
  }

  async deleteQuote(quoteId: string): Promise<any> {
    const response = await this.api.delete(`/quotes/${quoteId}`);
    return response.data;
  }

  // Plantillas: el texto formal (garantías, qué incluye, condiciones) que se repite en todas.
  async getQuoteTemplates(): Promise<any> {
    const response = await this.api.get('/quotes/templates');
    return response.data;
  }

  async createQuoteTemplate(data: any): Promise<any> {
    const response = await this.api.post('/quotes/templates', data);
    return response.data;
  }

  async updateQuoteTemplate(templateId: string, data: any): Promise<any> {
    const response = await this.api.put(`/quotes/templates/${templateId}`, data);
    return response.data;
  }

  async deleteQuoteTemplate(templateId: string): Promise<any> {
    const response = await this.api.delete(`/quotes/templates/${templateId}`);
    return response.data;
  }

  // ============ MONITOREO ============
  // El portal no dibuja gráficas —para eso está Grafana—: responde "¿hay algo ardiendo
  // ahora mismo?" en la pantalla que ya está abierta.

  async getMonitoringResumen(): Promise<any> {
    const response = await this.api.get('/monitoring/resumen');
    return response.data;
  }

  async getMonitoringSalud(): Promise<any> {
    const response = await this.api.get('/monitoring/salud');
    return response.data;
  }

  async getMonitoringReglas(): Promise<any> {
    const response = await this.api.get('/monitoring/reglas');
    return response.data;
  }

  // Device diagnostics
  async getDevicePositions(id: string, from?: string, to?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await this.api.get(`/devices/${id}/positions`, { params });
    return this.handleResponse(response);
  }

  async getDeviceTraccarInfo(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/devices/${id}/traccar-info`);
    return this.handleResponse(response);
  }

  async getDeviceTraccarDiagnostics(id: string, from: string, to: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/devices/${id}/traccar-diagnostics`, { params: { from, to }, timeout: 90000 });
    return this.handleResponse(response);
  }

  // Diagnóstico completo: cruza Traccar, SimBase y el portal. Va a petición
  // porque son varias llamadas a APIs externas y tarda unos segundos.
  async getDeviceDiagnostico(id: string, dias = 5): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/devices/${id}/diagnostico`, { params: { dias }, timeout: 120000 });
    return this.handleResponse(response);
  }

  async ejecutarAccionDiagnostico(id: string, accion: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/devices/${id}/diagnostico/accion`, { accion }, { timeout: 60000 });
    return this.handleResponse(response);
  }

  // Perfil de uso: dónde duerme el vehículo y por dónde se mueve.
  //
  // Va en dos pasos porque el cálculo tarda alrededor de un minuto y medio, y una
  // petición HTTP tan larga se cae en cualquier proxy o red móvil por el camino.
  // Se arranca, y después se pregunta por el avance hasta que esté.
  async iniciarPatron(id: string, dias = 45): Promise<{ jobId: string }> {
    const response = await this.api.post(`/devices/${id}/patron`, { dias }, { timeout: 30000 });
    return response.data;
  }

  async estadoPatron(id: string, jobId: string): Promise<any> {
    const response = await this.api.get(`/devices/${id}/patron/${jobId}`, { timeout: 20000 });
    return response.data;
  }

  // Bulk operations
  async bulkRenew(data: { deviceIds: string[]; paymentMethod?: string; paymentReference?: string; amount?: number; notes?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/renewals/bulk-renew', data);
    return this.handleResponse(response);
  }

  async bulkSimAction(data: { simCardIds: string[]; action: 'activate' | 'deactivate' }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/simcards/bulk-action', data);
    return this.handleResponse(response);
  }

  async getExpiredDevices(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/devices', { params: { status: 'expired', limit: 500 } });
    return this.handleResponse(response);
  }

  // ============ BOT / LEADS CRM ============
  async getBotConversations(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/bot/conversations', { params });
    return response.data;
  }

  async getBotConversation(phone: string): Promise<any> {
    const response = await this.api.get(`/bot/conversations/${phone}`);
    return response.data;
  }

  async sendBotMessage(phone: string, message: string): Promise<any> {
    const response = await this.api.post(`/bot/conversations/${phone}/send`, { message, sender: 'assistant' });
    return response.data;
  }

  async pauseBot(phone: string, minutes: number): Promise<any> {
    const response = await this.api.post(`/bot/conversations/${phone}/pause`, { minutes });
    return response.data;
  }

  async resumeBot(phone: string): Promise<any> {
    const response = await this.api.post(`/bot/conversations/${phone}/resume`);
    return response.data;
  }

  async setFlowStage(phone: string, flowStage: string): Promise<any> {
    const response = await this.api.put(`/bot/conversations/${phone}/flowStage`, { flowStage });
    return response.data;
  }

  // Acción en lote sobre varias conversaciones (tablero Kanban).
  // op ∈ flowStage | status | clientZone | followup | label | archive | action | send | route
  async bulkConversations(phones: string[], op: string, payload?: Record<string, any>): Promise<any> {
    const response = await this.api.post('/bot/conversations/bulk', { phones, op, payload: payload || {} });
    return response.data;
  }

  async archiveConversation(phone: string, archived: boolean = true): Promise<any> {
    const response = await this.api.patch(`/bot/conversations/${phone}`, { kanbanArchived: archived });
    return response.data;
  }

  async getBotStatus(): Promise<any> {
    const response = await this.api.get('/bot/bot-status');
    return response.data;
  }

  // Interruptor global: apaga solo las respuestas automáticas del bot.
  // Los follow-ups a clientes y los avisos de ruta siguen funcionando igual.
  async getGlobalPause(): Promise<any> {
    const response = await this.api.get('/bot/global-pause');
    return response.data;
  }

  async setGlobalPause(paused: boolean): Promise<any> {
    const response = await this.api.post('/bot/global-pause', { paused });
    return response.data;
  }

  // ============ BOT: ROUTE QUEUE ============
  async getRouteQueue(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/bot/route-queue', { params });
    return response.data;
  }

  async confirmRouteQueue(zone: string, date: string, message?: string): Promise<any> {
    const response = await this.api.post('/bot/route-queue/confirm', { zone, date, message });
    return response.data;
  }

  async confirmRouteQueueClient(phone: string, data: { date?: string; timePreference?: string }): Promise<any> {
    const response = await this.api.put(`/bot/route-queue/${phone}/confirm`, data);
    return response.data;
  }

  async revertRouteQueueClient(phone: string): Promise<any> {
    const response = await this.api.put(`/bot/route-queue/${phone}/revert`);
    return response.data;
  }

  async addToRouteQueue(phone: string, zone: string, name?: string): Promise<any> {
    const response = await this.api.post('/bot/route-queue/add', { phone, zone, name });
    return response.data;
  }

  async removeFromRouteQueue(phone: string): Promise<any> {
    const response = await this.api.delete(`/bot/route-queue/${phone}`);
    return response.data;
  }

  async getRouteQueueConfig(): Promise<any> {
    const response = await this.api.get('/bot/route-queue/config');
    return response.data;
  }

  async setRouteQueueConfig(zone: string, tentativeDate: string | null): Promise<any> {
    const response = await this.api.put(`/bot/route-queue/config/${zone}`, { tentativeDate });
    return response.data;
  }

  // ============ BOT: ROUTES ============
  async getBotRoutes(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/bot/routes', { params });
    return response.data;
  }

  async createBotRoute(data: { region: string; date: string; capacity?: number; departureTime?: string }): Promise<any> {
    const response = await this.api.post('/bot/routes', data);
    return response.data;
  }

  async updateBotRoute(routeId: string, data: Record<string, any>): Promise<any> {
    const response = await this.api.put(`/bot/routes/${routeId}`, data);
    return response.data;
  }

  async addClientToRoute(routeId: string, data: { phone: string; name: string; zone?: string; vehicleCount?: number; address?: string }): Promise<any> {
    const response = await this.api.post(`/bot/routes/${routeId}/clients`, data);
    return response.data;
  }

  async removeClientFromRoute(routeId: string, phone: string): Promise<any> {
    const response = await this.api.delete(`/bot/routes/${routeId}/clients/${phone}`);
    return response.data;
  }

  async getRouteSlots(routeId: string): Promise<any> {
    const response = await this.api.get(`/bot/routes/${routeId}/slots`);
    return response.data;
  }

  async getRouteRegions(): Promise<any> {
    const response = await this.api.get('/bot/routes/regions');
    return response.data;
  }

  // ============ BOT: KNOWLEDGE BASE ============
  async getBotKnowledge(): Promise<any> {
    const response = await this.api.get('/bot/knowledge');
    return response.data;
  }

  async getBotKnowledgeFile(filename: string): Promise<any> {
    const response = await this.api.get(`/bot/knowledge/${filename}`);
    return response.data;
  }

  async updateBotKnowledgeFile(filename: string, content: string): Promise<any> {
    const response = await this.api.put(`/bot/knowledge/${filename}`, { content });
    return response.data;
  }

  async reindexKnowledgeBase(): Promise<any> {
    const response = await this.api.post('/bot/knowledge/reindex');
    return response.data;
  }

  // ============ BOT: INSIGHTS & REPORTS ============
  async getBotInsights(): Promise<any> {
    const response = await this.api.get('/bot/insights');
    return response.data;
  }

  async getBotLeadReports(): Promise<any> {
    const response = await this.api.get('/bot/lead-reports');
    return response.data;
  }

  // ============ BOT: LABELS ============
  async getBotLabels(): Promise<any> {
    const response = await this.api.get('/bot/labels');
    return response.data;
  }

  async getConversationLabels(phone: string): Promise<any> {
    const response = await this.api.get(`/bot/conversations/${phone}/labels`);
    return response.data;
  }

  async setConversationLabel(phone: string, label: string, action: 'add' | 'remove' = 'add'): Promise<any> {
    const response = await this.api.post(`/bot/conversations/${phone}/labels`, { label, action });
    return response.data;
  }

  // ============ BOT: NOTES ============
  async updateConversationNotes(phone: string, notes: string): Promise<any> {
    const response = await this.api.put(`/bot/conversations/${phone}/notes`, { notes });
    return response.data;
  }

  // ============ BOT: STATUS ============
  async updateConversationStatus(phone: string, status: string): Promise<any> {
    const response = await this.api.put(`/bot/conversations/${phone}/status`, { status });
    return response.data;
  }

  // ============ BOT: FOLLOW-UPS & ACTIONS ============
  async getBotFollowups(): Promise<any> {
    const response = await this.api.get('/bot/followups');
    return response.data;
  }

  async getConversationActions(phone: string): Promise<any> {
    const response = await this.api.get(`/bot/conversations/${phone}/actions`);
    return response.data;
  }

  async createConversationAction(phone: string, data: { message: string; scheduledFor: string; type?: string; name?: string }): Promise<any> {
    const response = await this.api.post(`/bot/conversations/${phone}/actions`, data);
    return response.data;
  }

  async cancelConversationAction(phone: string, actionId: string): Promise<any> {
    const response = await this.api.delete(`/bot/conversations/${phone}/actions/${actionId}`);
    return response.data;
  }

  async setConversationFollowUp(phone: string, followUp: string | null, followUpAt?: string): Promise<any> {
    const response = await this.api.put(`/bot/conversations/${phone}/followup`, { followUp, followUpAt });
    return response.data;
  }

  async patchConversation(phone: string, fields: Record<string, string>): Promise<any> {
    const response = await this.api.patch(`/bot/conversations/${phone}`, fields);
    return response.data;
  }

  // ============ BOT: SEND MESSAGE ============
  async sendWhatsAppMessage(to: string, message: string): Promise<any> {
    const response = await this.api.post('/bot/send-message', { to, message });
    return response.data;
  }

  // ============ REDES SOCIALES (Command Center) ============
  async getSocialOrganic(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/social/organic');
    return this.handleResponse(response);
  }
  async getSocialFunnel(params?: { from?: string; to?: string; margen?: number }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/social/funnel', { params });
    return this.handleResponse(response);
  }
  async getSocialSummary(params?: { from?: string; to?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/social/summary', { params });
    return this.handleResponse(response);
  }
  async getSocialFunnelByZone(params?: { from?: string; to?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/social/funnel/by-zone', { params });
    return this.handleResponse(response);
  }
  async getSocialPosts(params?: { status?: string; channel?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.get('/social/posts', { params });
    return this.handleResponse(response);
  }
  async createSocialPost(body: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/social/posts', body);
    return this.handleResponse(response);
  }
  async updateSocialPost(id: string, body: any): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/social/posts/${id}`, body);
    return this.handleResponse(response);
  }
  async deleteSocialPost(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/social/posts/${id}`);
    return this.handleResponse(response);
  }
  async publishSocialPostNow(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/social/posts/${id}/publish-now`);
    return this.handleResponse(response);
  }
  async uploadSocialMedia(files: File[]): Promise<ApiResponse<any>> {
    const formData = new FormData();
    files.forEach((f) => formData.append('social', f));
    const response = await this.api.post('/social/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return this.handleResponse(response);
  }

  // ——— Comisiones por referidos (quien trae clientes y cobra por instalación) ———
  async getReferralPartners(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/referral-commissions');
    return this.handleResponse(response);
  }
  async getReferralInstallations(id: string, todas = false): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/referral-commissions/${id}/installations`, {
      params: todas ? { todas: 1 } : undefined,
    });
    return this.handleResponse(response);
  }
  async createReferralPartner(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/referral-commissions', data);
    return this.handleResponse(response);
  }
  async updateReferralPartner(id: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/referral-commissions/${id}`, data);
    return this.handleResponse(response);
  }
  async payReferralPartner(id: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/referral-commissions/${id}/payouts`, data);
    return this.handleResponse(response);
  }
  async deleteReferralPartner(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/referral-commissions/${id}`);
    return this.handleResponse(response);
  }
  async deleteReferralPayout(id: string, payoutId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/referral-commissions/${id}/payouts/${payoutId}`);
    return this.handleResponse(response);
  }
}

export const apiService = new ApiService();
export default apiService;